// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect } from 'react';

import { useEvolvePost } from '@evolvemep/api-ts';
import { Loader, Text, Flex } from '@mantine/core';
import { notifications } from '@mantine/notifications';
// oxlint-disable-next-line no-restricted-imports
import { useNavigate, useSearchParams } from 'react-router';

import { firstNonEmpty } from 'helpers/firstNonEmpty';
import { useLocalStorage } from 'hooks/useLocalStorage';

import { autodeskRedirectUri, extractAutodeskStateVar } from './helpers';
import { useAutodeskAuth } from './useAutodeskAuth';

/**
 * After authenticating inside of Autodesk Cloud,
 * Autodesk redirects the user back to this page with a `code` param.
 * We use that `code` param to get an actual access token.
 */
export const AutodeskAuth = () => {
  const navigate = useNavigate();
  const [, setAutodeskAuthRejected] = useLocalStorage('AUTODESK_AUTH_REJECTED', false);
  const { redirectToAutodeskLogin, setAutodeskAuth } = useAutodeskAuth();

  const { apiCall: getAutodeskAuth } = useEvolvePost('shop/autodesk/auth');

  const [params] = useSearchParams();
  const code = params.get('code');
  const state = params.get('state');
  // If `error_description` is empty, try pulling `error`
  const error = firstNonEmpty(params.get('error_description'), params.get('error'));

  useEffect(() => {
    setAutodeskAuthRejected(!code);

    const { previousLoc } = extractAutodeskStateVar(state ?? '');
    // If we somehow came from this page, just redirect to root
    const route = previousLoc === window.location.pathname || !previousLoc ? '/' : previousLoc;
    if (code) {
      void getAutodeskAuth({ code, redirectUri: autodeskRedirectUri })
        .then(setAutodeskAuth)
        .then(() => navigate(route, { replace: true }));
    } else {
      void navigate(route, { replace: true });
      notifications.show({
        title: 'Autodesk authentication failed.',
        autoClose: 10000,
        message: (
          <>
            {error?.replaceAll('+', ' ') ?? 'Something went wrong.'}
            {previousLoc !== '/' && (
              <>
                <br />
                <br />
                <Text
                  className="link"
                  onClick={() =>
                    redirectToAutodeskLogin({
                      forceLogin: true,
                    })
                  }
                >
                  Try again?
                </Text>
              </>
            )}
          </>
        ),
        color: 'red',
      });
    }
  }, [
    code,
    error,
    getAutodeskAuth,
    navigate,
    redirectToAutodeskLogin,
    setAutodeskAuth,
    setAutodeskAuthRejected,
    state,
  ]);

  return (
    <Flex h="100vh" justify="center" align="center">
      <Loader />
    </Flex>
  );
};
